// Every job, resolved, for the screens that look across jobs (Home, the job list,
// purchasing, the Landing Board). A live job resolves at current standards; a locked one
// at the version it was locked on.

import { useMemo } from 'react';
import { buildJob, buildLibrary } from '@zerogap/db';
import { STEP, jobTier, resolveJob, type Library, type Resolution, type ResolveOptions, type Tier } from '@zerogap/engine';
import { useRows, type Row } from './api';
import { baseBuild, pricingOptions } from './job';
import { useLibraryRows } from './library';

export interface JobSummary {
  job: Row;
  opp: Row | null;
  stage: Row | null;
  res: Resolution | null;
  itemCount: number;
  /** How sure the job's quantities are: firm (a locked revision), committed (won), likely (open), none (lost). */
  tier: Tier;
  /** Done: lost, not a lead, retired, or at the Closed step. The Jobs tab's Done list. */
  done: boolean;
  /** Retired (decision 1, 2026-10-19): counts nowhere, in no lead's value, on no list but Done; or its opportunity is retired. */
  retired: boolean;
  /** The job's Builds and Add-ons, in order, and the job priced another way (a Build, an Add-on taken). `res` is the base Build's price (2026-10-17). */
  builds: Row[];
  addOns: Row[];
  resolveWith: (opts: ResolveOptions) => Resolution | null;
}

/** One way a job is priced: a Build, alone or with one Add-on taken. */
export interface JobPricing {
  key: string;
  label: string;
  build: Row | null;
  addOn: Row | null;
}

/**
 * Every way the job is priced, as the job header's Pricing picker offers it: each Build (the base first; the job as
 * entered when it has none), and each Build with each Add-on taken. The first is the job's own price.
 */
export function jobPricings(j: JobSummary): JobPricing[] {
  const builds: (Row | null)[] = j.builds.length > 0 ? [...j.builds.filter((b) => b.is_base), ...j.builds.filter((b) => !b.is_base)] : [null];
  const out: JobPricing[] = [];
  for (const b of builds) {
    const name = b ? String(b.name) : 'As entered';
    out.push({ key: b ? String(b.id) : 'entered', label: name, build: b, addOn: null });
    for (const a of j.addOns) out.push({ key: `${b ? b.id : 'entered'}+${a.id}`, label: `${name} + ${a.name}`, build: b, addOn: a });
  }
  return out;
}

/** The job priced one of those ways; the job's own price (its base Build, with no Add-on) when that is what is asked. */
export function pricedAs(j: JobSummary, p: JobPricing): Resolution | null {
  if (!p.addOn && (p.build?.id ?? null) === (baseBuild(j.builds)?.id ?? null)) return j.res;
  return j.resolveWith(pricingOptions(p.build, p.addOn ? [p.addOn] : []));
}

export function useAllJobs(): { loading: boolean; jobs: JobSummary[]; steps: Row[]; lib: Library | null } {
  const lib = useLibraryRows();
  const jobs = useRows('job', { order: ['created_at'] });
  const opps = useRows('opportunity');
  const steps = useRows('workflow_step', { order: ['sort'] });
  const standards = useRows('job_standard');
  const items = useRows('job_item', { order: ['sort', 'created_at'] });
  const subs = useRows('sub_line');
  const decisions = useRows('decision_line');
  const costs = useRows('job_cost_line');
  const rooms = useRows('room');
  const roomStandards = useRows('room_standard');
  const typeSteps = useRows('job_type_route_step');
  const typeBom = useRows('job_type_bom_line');
  const routeLines = useRows('job_route_line');
  const bomLines = useRows('job_bom_line');
  const revisions = useRows('revision');
  const builds = useRows('build', { order: ['sort', 'created_at'] });
  const addOns = useRows('add_on', { order: ['sort', 'created_at'] });
  const loading = builds.loading || addOns.loading || lib.loading || jobs.loading || opps.loading || steps.loading || standards.loading || items.loading || subs.loading || decisions.loading || costs.loading || rooms.loading || roomStandards.loading || typeSteps.loading || typeBom.loading || routeLines.loading || bomLines.loading || revisions.loading;

  return useMemo(() => {
    if (loading || lib.rows.standards_version.length === 0) return { loading, jobs: [], steps: steps.rows, lib: null };
    const libs = new Map<string, Library>();
    const libFor = (versionId: string | null): Library | null => {
      const key = versionId ?? 'current';
      if (!libs.has(key)) {
        try {
          libs.set(key, buildLibrary(lib.rows, versionId));
        } catch {
          return null;
        }
      }
      return libs.get(key)!;
    };
    const by = (rows: Row[], jobId: string) => rows.filter((r) => r.job_id === jobId);
    // a retired build or add-on counts nowhere (2026-10-29)
    const liveBuilds = builds.rows.filter((b) => !b.retired_at);
    const liveAddOns = addOns.rows.filter((a) => !a.retired_at);
    const locked = new Set(revisions.rows.map((r) => r.job_id as string));
    const closed = steps.rows.find((s) => s.key === STEP.closed);
    const firstLive = steps.rows.find((s) => !s.retired_at) ?? null;
    const out: JobSummary[] = jobs.rows.map((job) => {
      const l = libFor(job.standards_version_id ?? null);
      let res: Resolution | null = null;
      let resolveWith: (opts: ResolveOptions) => Resolution | null = () => null;
      const jobItems = by(items.rows, job.id);
      if (l) {
        try {
          const jobRooms = by(rooms.rows, job.id);
          const roomIds = new Set(jobRooms.map((r) => r.id));
          const jobIn = buildJob(
              {
                job, job_standard: by(standards.rows, job.id), job_item: jobItems, sub_line: by(subs.rows, job.id), decision_line: by(decisions.rows, job.id), job_cost_line: by(costs.rows, job.id),
                room: jobRooms, room_standard: roomStandards.rows.filter((s) => roomIds.has(s.room_id)),
                job_type_route_step: typeSteps.rows, job_type_bom_line: typeBom.rows, job_route_line: by(routeLines.rows, job.id), job_bom_line: by(bomLines.rows, job.id),
              },
              lib.rows.standard_def,
          );
          // the job's own price is its base Build's: its overrides and percentages apply (2026-10-17)
          res = resolveJob(l, jobIn, pricingOptions(baseBuild(by(liveBuilds, job.id)), []));
          resolveWith = (opts) => {
            try {
              return resolveJob(l, jobIn, opts);
            } catch {
              return null;
            }
          };
        } catch {
          res = null;
        }
      }
      const opp = opps.rows.find((o) => o.id === job.opportunity_id) ?? null;
      const tier = jobTier({ has_revision: locked.has(job.id), outcome: opp?.outcome });
      // a retired job, or a job of a retired opportunity, counts nowhere (decision 1, 2026-10-19)
      const retired = !!job.retired_at || !!opp?.retired_at;
      return {
        job,
        opp,
        // a step retired into the pipeline (2026-10-19) is no job's step: a job left on one reads as on the first live step
        stage: steps.rows.find((s) => s.id === job.stage_id && !s.retired_at) ?? (job.stage_id ? firstLive : null),
        res,
        itemCount: jobItems.filter((i) => !i.parent_item_id).length,
        tier,
        done: retired || tier === 'none' || (!!closed && job.stage_id === closed.id),
        retired,
        builds: by(liveBuilds, job.id),
        addOns: by(liveAddOns, job.id),
        resolveWith,
      };
    });
    return { loading: false, jobs: out, steps: steps.rows, lib: libFor(null) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, lib.rows, jobs.rows, opps.rows, steps.rows, standards.rows, items.rows, subs.rows, decisions.rows, costs.rows, rooms.rows, roomStandards.rows, typeSteps.rows, typeBom.rows, routeLines.rows, bomLines.rows, revisions.rows, builds.rows, addOns.rows]);
}

export function jobTitle(j: { job: Row; opp: Row | null }): string {
  return `${j.opp?.name ?? 'No opportunity'} · ${j.job.scope_name}`;
}
